import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Loader2, Mail, X, Send, CheckCheck, Eye, MousePointerClick, AlertTriangle, Link2 } from "lucide-react";

export const EMAIL_STATUS = {
  gesendet: { label: "Gesendet", cls: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: Send },
  zugestellt: { label: "Zugestellt", cls: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCheck },
  "geöffnet": { label: "Geöffnet", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Eye },
  geklickt: { label: "Geklickt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: MousePointerClick },
  angesehen: { label: "Angesehen", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: Eye },
  bounced: { label: "Unzustellbar", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  beschwerde: { label: "Spam-Beschwerde", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  fehlgeschlagen: { label: "Fehlgeschlagen", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
};

export function EmailStatusBadge({ status, testId }) {
  const s = EMAIL_STATUS[status] || EMAIL_STATUS.gesendet;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${s.cls}`} data-testid={testId}>
      <Icon size={10}/> {s.label}
    </span>
  );
}

const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null);

export function EmailHistory({ emails }) {
  if (!emails?.length) return null;
  return (
    <div className="border border-zinc-200 divide-y divide-zinc-100" data-testid="email-history">
      {[...emails].reverse().map((em) => (
        <div key={em.id} className="px-3 py-2.5 text-sm" data-testid={`email-entry-${em.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <span className="font-medium text-zinc-900">{em.docType === "confirmation" ? "AB / Proforma" : em.docType === "reminder" ? "Erinnerung · Angebot" : "Angebot"} {em.docNumber}</span>
              {em.docType === "reminder" && <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">{em.sentBy === "Automatisch" ? "automatisch" : `manuell · ${em.sentBy || ""}`}</span>}
              <span className="text-zinc-500"> an {em.to?.join(", ")}</span>
            </div>
            <EmailStatusBadge status={em.status} testId={`email-status-${em.id}`} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-zinc-500 mt-1 font-mono">
            <span>Gesendet {fmtTs(em.sentAt)}</span>
            {em.deliveredAt && <span className="text-blue-700">Zugestellt {fmtTs(em.deliveredAt)}</span>}
            {em.openedAt && <span className="text-emerald-700">Geöffnet {fmtTs(em.openedAt)}</span>}
            {em.clickedAt && <span className="text-emerald-700">Geklickt {fmtTs(em.clickedAt)}</span>}
            {em.viewedAt && <span className="text-emerald-800 font-semibold">Online angesehen {fmtTs(em.viewedAt)}</span>}
            {em.failReason && <span className="text-red-600">{em.failReason}</span>}
          </div>
          {em.viewUrl && (
            <a href={em.viewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 mt-1">
              <Link2 size={10}/> Kundenlink öffnen
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

export function EmailDialog({ quoteId, quoteNumber, customerEmail, hasConfirmation, confirmationNumber, onClose, onSent }) {
  const [docType, setDocType] = useState("angebot");
  const [to, setTo] = useState(customerEmail || "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(`Angebot ${quoteNumber}`);
  const [message, setMessage] = useState(`anbei erhalten Sie unser Angebot ${quoteNumber} als PDF.\nBei Fragen stehen wir Ihnen gerne zur Verfügung.`);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const switchType = (t) => {
    setDocType(t);
    const num = t === "confirmation" ? confirmationNumber : quoteNumber;
    setSubject(t === "confirmation" ? `Auftragsbestätigung / Proforma-Rechnung ${num}` : `Angebot ${num}`);
    setMessage(t === "confirmation"
      ? `anbei erhalten Sie Ihre Auftragsbestätigung / Proforma-Rechnung ${num} als PDF.\nBei Fragen stehen wir Ihnen gerne zur Verfügung.`
      : `anbei erhalten Sie unser Angebot ${num} als PDF.\nBei Fragen stehen wir Ihnen gerne zur Verfügung.`);
  };
  const split = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const send = async () => {
    setSending(true); setError("");
    try {
      const r = await api.post(`/quotes/${quoteId}/email`, { to: split(to), cc: split(cc), subject, message, docType });
      onSent(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };
  const inp = "w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="email-dialog">
      <div className="bg-white w-full max-w-lg border border-zinc-300 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-zinc-800"><Mail size={14}/> Per E-Mail senden</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="email-dialog-close"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3">
          {hasConfirmation && (
            <div className="flex gap-2 text-xs">
              {[["angebot", `Angebot ${quoteNumber}`], ["confirmation", `AB / Proforma ${confirmationNumber}`]].map(([k, l]) => (
                <button key={k} onClick={() => switchType(k)} data-testid={`email-doctype-${k}`}
                        className={`px-3 py-1.5 border rounded-sm ${docType === k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700"}`}>{l}</button>
              ))}
            </div>
          )}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">An (E-Mail, mehrere mit Komma)</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} className={inp} data-testid="email-to" placeholder="kunde@example.de" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">CC (optional)</label>
            <input value={cc} onChange={(e) => setCc(e.target.value)} className={inp} data-testid="email-cc" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Betreff</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inp} data-testid="email-subject" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Nachricht (nach der Anrede)</label>
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className={inp} data-testid="email-message" />
          </div>
          <p className="text-[11px] text-zinc-500">Das PDF wird angehängt und zusätzlich als Online-Link mitgeschickt. Zustellung, Öffnung und Klick auf den Link werden im Verlauf angezeigt.</p>
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="email-error">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200 bg-zinc-50">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-zinc-300 rounded-sm hover:bg-white">Abbrechen</button>
          <button onClick={send} disabled={sending || !split(to).length} data-testid="email-send-button"
                  className="text-sm font-medium px-4 py-2 bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2">
            {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Senden
          </button>
        </div>
      </div>
    </div>
  );
}
